import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import axios from 'axios'
import {
  TileMappingConfig,
  TileSpecification,
} from 'src/queries/agent/network/agent.models'
import { getRequestConfig } from 'src/queries/requestConfig'

/**
 * Generic response type for dynamic tile data
 * The actual structure depends on the endpoint and config mapping
 */
export type TileDataResponse = Record<string, any>

/**
 * Fetches data from a dynamic endpoint specified in the tile specification
 * @param endpoint - API endpoint path (e.g., "/clickhouse/insights/spendbyexpensecategorylabel")
 * @param params - Query parameters for the endpoint
 * @returns Raw response data from the endpoint
 */
const fetchTileData = async (
  endpoint: string,
  params: Record<string, unknown>,
): Promise<TileDataResponse> => {
  const requestConfig = getRequestConfig()

  const response = await axios.get<TileDataResponse>(endpoint, {
    ...requestConfig,
    params,
  })

  return response.data
}

/**
 * Helper function to safely get nested values from an object using dot notation
 * TODO: we should make this an explicit type based on the config mapping
 * @param obj - Object to extract value from
 * @param path - Dot-separated path (e.g., "data.total", "result.items[0].value")
 * @returns Value at the path or undefined if not found
 */
export const getNestedValue = (obj: any, path: string): any => {
  if (!path) return obj

  const parts = path.split('.')
  let current = obj

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined
    }
    // Handle array notation like "items[0]"
    const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/)
    if (arrayMatch) {
      const [, key, index] = arrayMatch
      current = current[key]?.[parseInt(index, 10)]
    } else {
      current = current[part]
    }
  }

  return current
}

/**
 * Maps raw API response to structured data using the tile mapping config
 * @param rawData - Raw response from the API
 * @param config - Mapping configuration specifying how to extract values
 * @returns Mapped data object with extracted values
 */
export const mapTileData = (
  rawData: TileDataResponse,
  config: TileMappingConfig,
): Record<string, any> => {
  const mapped: Record<string, any> = {
    raw: rawData, // Always include raw data for debugging
  }

  // Extract standard mappings
  if (config.valueKey) {
    mapped.value = getNestedValue(rawData, config.valueKey)
  }
  if (config.labelKey) {
    mapped.label = getNestedValue(rawData, config.labelKey)
  }
  if (config.dataArrayKey) {
    mapped.dataArray = getNestedValue(rawData, config.dataArrayKey)
  }
  if (config.xAxisKey) {
    mapped.xAxis = getNestedValue(rawData, config.xAxisKey)
  }
  if (config.yAxisKey) {
    mapped.yAxis = getNestedValue(rawData, config.yAxisKey)
  }

  // Extract custom mappings
  if (config.customMappings) {
    Object.entries(config.customMappings).forEach(([key, path]) => {
      mapped[key] = getNestedValue(rawData, path)
    })
  }

  return mapped
}

/**
 * Dynamic useQuery hook for fetching and mapping tile data based on TileSpecification
 *
 * This hook creates a React Query that:
 * 1. Fetches data from the specified endpoint with given params
 * 2. Maps the response using the config mapping keys
 * 3. Returns both raw and mapped data for flexibility
 *
 * @param tileSpec - Tile specification from the insights backend
 * @param options - Additional React Query options
 * @returns Query result with raw and mapped data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useDynamicTileQuery(tileSpec)
 *
 * // Access mapped data
 * const value = data?.mapped.value
 * const dataArray = data?.mapped.dataArray
 *
 * // Access raw response for debugging
 * console.log('Raw API response:', data?.raw)
 * ```
 */
export const useDynamicTileQuery = (
  tileSpec: TileSpecification | null | undefined,
  options?: Omit<
    UseQueryOptions<
      { raw: TileDataResponse; mapped: Record<string, any> },
      Error,
      { raw: TileDataResponse; mapped: Record<string, any> },
      [string, string, Record<string, unknown>]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    // TODO: octopod-ai - move to query keys constant file
    queryKey: ['dynamicTile', tileSpec?.endpoint ?? '', tileSpec?.params ?? {}],
    queryFn: async () => {
      if (!tileSpec) {
        throw new Error('Tile specification is required')
      }

      const rawData = await fetchTileData(tileSpec.endpoint, tileSpec.params)
      const mappedData = mapTileData(rawData, tileSpec.config)

      return {
        raw: rawData,
        mapped: mappedData,
      }
    },
    enabled: !!tileSpec?.endpoint,
    ...options,
  })
}
